import { useCallback, useEffect, useRef } from 'react';

import useCurrentTime from '@/hooks/useCurrentTime';
import useCustomServerTimeSync from '@/hooks/useCustomServerTimeSync';

const useComponentTracking = (syncTimeOnVisibility: boolean) => {
  const trackingStartTimeReference = useRef<number>();
  const visibleTimeReference = useRef(0);
  const lastVisibleStartTimeReference = useRef<number>();
  const isTracking = useRef<boolean>(false);
  const { getCurrentTime } = useCurrentTime();
  const { syncServerTime } = useCustomServerTimeSync();

  const updateVisibleTime = useCallback(() => {
    const currentTime = getCurrentTime();
    if (document.visibilityState === 'hidden' && lastVisibleStartTimeReference.current) {
      visibleTimeReference.current += currentTime - lastVisibleStartTimeReference.current;
      lastVisibleStartTimeReference.current = undefined;
    } else if (document.visibilityState === 'visible') {
      lastVisibleStartTimeReference.current = currentTime;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      void (async () => {
        if (document.visibilityState === 'visible' && syncTimeOnVisibility) {
          await syncServerTime();
        }
        if (isTracking.current) {
          updateVisibleTime();
        }
      })();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      const currentTime = getCurrentTime();
      if (lastVisibleStartTimeReference.current && isTracking.current) {
        visibleTimeReference.current += currentTime - lastVisibleStartTimeReference.current;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateVisibleTime, syncServerTime, syncTimeOnVisibility]);

  const getVisibleDuration = useCallback(() => {
    const currentTime = getCurrentTime();
    const visibleDuration =
      visibleTimeReference.current +
      (document.visibilityState === 'visible' && lastVisibleStartTimeReference.current
        ? currentTime - lastVisibleStartTimeReference.current
        : 0);
    return visibleDuration;
  }, [getCurrentTime]);

  const getVisibleDurationAndCurrentTime = useCallback(() => {
    const currentTime = getCurrentTime();
    const visibleDuration =
      visibleTimeReference.current +
      (document.visibilityState === 'visible' && lastVisibleStartTimeReference.current
        ? currentTime - lastVisibleStartTimeReference.current
        : 0);
    return { visibleDuration, currentTime };
  }, [getCurrentTime]);

  const getTrackingDuration = useCallback(() => {
    if (!trackingStartTimeReference.current) return 0;
    return getCurrentTime() - trackingStartTimeReference.current;
  }, [getCurrentTime]);

  const getTrackingStartTime = useCallback(() => trackingStartTimeReference.current, []);
  const getLastVisibleTime = useCallback(() => lastVisibleStartTimeReference.current, []);
  const getAccumulatedVisibleTime = useCallback(() => visibleTimeReference.current, []);

  const startTracking = useCallback(() => {
    const currentTime = getCurrentTime();
    isTracking.current = true;
    trackingStartTimeReference.current = currentTime;
    lastVisibleStartTimeReference.current = currentTime;
  }, [getCurrentTime]);

  const stopTracking = useCallback(() => {
    const currentTime = getCurrentTime();
    visibleTimeReference.current +=
      document.visibilityState === 'visible' && lastVisibleStartTimeReference.current
        ? currentTime - (lastVisibleStartTimeReference.current || 0)
        : 0;
    trackingStartTimeReference.current = undefined;
    lastVisibleStartTimeReference.current = undefined;
    isTracking.current = false;
  }, [getCurrentTime]);

  const resetTracking = useCallback(() => {
    isTracking.current = false;
    visibleTimeReference.current = 0;
    trackingStartTimeReference.current = undefined;
    lastVisibleStartTimeReference.current = undefined;
  }, []);

  return {
    getVisibleDuration,
    getTrackingDuration,
    getTrackingStartTime,
    getLastVisibleTime,
    getAccumulatedVisibleTime,
    startTracking,
    stopTracking,
    resetTracking,
    getVisibleDurationAndCurrentTime,
  };
};

export default useComponentTracking;
